import { useEffect, useRef, useState } from 'react';

const services = [
  {
    title: '通所介護（デイサービス）',
    price: '要介護度により異なります',
    priceNote: '自己負担：1割〜3割',
    description: '日常生活の支援・機能訓練・レクリエーション活動などを提供します。',
    details: ['入浴介助', '食事提供（昼食・おやつ）', '機能訓練', 'レクリエーション', '健康チェック'],
    image: 'https://readdy.ai/api/search-image?query=Japanese%20dayservice%20center%20elderly%20group%20activity%20room%20bright%20cheerful%20seniors%20doing%20crafts%20together%20round%20tables%20natural%20light%20clean%20modern%20interior%20staff%20assisting%20warm%20community%20atmosphere%20flowers%20on%20table&width=500&height=380&seq=svc001&orientation=landscape',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </svg>
    ),
  },
  {
    title: '入浴サービス',
    price: '通所介護費に含む',
    priceNote: '追加費用なし',
    description: '安全に配慮した個別入浴介助を行います。機械浴・一般浴に対応しています。',
    details: ['個別入浴介助', '機械浴対応', '一般浴対応', '入浴前後の健康確認', 'タオル・用品貸出'],
    image: 'https://readdy.ai/api/search-image?query=Japanese%20care%20facility%20accessible%20bathroom%20clean%20modern%20bathing%20room%20grab%20bars%20safety%20equipment%20bright%20white%20tiles%20warm%20lighting%20professional%20accessible%20design%20no%20people%20clean%20hygienic&width=500&height=380&seq=svc002&orientation=landscape',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 12h16" />
        <path d="M4 12a8 8 0 0 1 16 0" />
        <path d="M2 20h20" />
        <path d="M6 20v-4" />
        <path d="M18 20v-4" />
        <path d="M10 20v-4" />
        <path d="M14 20v-4" />
      </svg>
    ),
  },
  {
    title: '機能訓練',
    price: '通所介護費に含む',
    priceNote: '追加費用なし',
    description: '理学療法士・機能訓練指導員が個別プログラムを作成し、日常生活動作の維持・向上を支援します。',
    details: ['個別機能訓練計画', '歩行訓練', '筋力維持運動', 'バランス訓練', '日常動作練習'],
    image: 'https://readdy.ai/api/search-image?query=Japanese%20physical%20therapy%20rehabilitation%20room%20elderly%20woman%20doing%20gentle%20exercise%20with%20therapist%20bright%20clean%20room%20exercise%20equipment%20parallel%20bars%20natural%20light%20professional%20setting%20warm%20encouraging%20atmosphere&width=500&height=380&seq=svc003&orientation=landscape',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="5" r="3" />
        <path d="M6.5 8a2 2 0 0 0-1.905 1.46L3 14h2l1 7h12l1-7h2l-1.595-4.54A2 2 0 0 0 17.5 8z" />
      </svg>
    ),
  },
  {
    title: '送迎サービス',
    price: '通所介護費に含む',
    priceNote: '追加費用なし',
    description: 'ご自宅から施設まで、専用車両でお迎え・お送りします。車椅子対応車両も完備しています。',
    details: ['自宅送迎', '車椅子対応車両', '乗降介助', '時間調整対応', '安全運転の徹底'],
    image: 'https://readdy.ai/api/search-image?query=Japanese%20care%20facility%20accessible%20minivan%20white%20vehicle%20staff%20helping%20elderly%20person%20board%20residential%20neighborhood%20morning%20sunshine%20clean%20professional%20transportation%20service%20gentle%20assistance%20suburban%20Japan&width=500&height=380&seq=svc004&orientation=landscape',
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="1" y="3" width="15" height="13" rx="2" />
        <path d="M16 8h4l3 3v5h-7V8z" />
        <circle cx="5.5" cy="18.5" r="2.5" />
        <circle cx="18.5" cy="18.5" r="2.5" />
      </svg>
    ),
  },
];

function useInView(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
}

export default function ServicesSection() {
  const { ref, inView } = useInView();

  return (
    <section id="services" className="py-24 md:py-32 bg-[#f8faf6]">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div ref={ref} className="mb-14 md:mb-18">
          <div
            className={`flex items-center gap-2 mb-4 transition-all duration-700 ${
              inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <div className="h-px w-8 bg-[#88B277]" />
            <span className="text-[#88B277] text-sm font-semibold tracking-widest uppercase">Services &amp; Fees</span>
          </div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <h2
              className={`text-3xl md:text-4xl font-bold text-gray-800 transition-all duration-700 delay-100 ${
                inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              サービス内容と<span className="text-[#88B277]">料金目安</span>
            </h2>
            <p
              className={`text-gray-500 text-sm max-w-sm leading-relaxed transition-all duration-700 delay-200 ${
                inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              料金は介護保険の適用により、要介護度・所得に応じて異なります。
              詳細はお気軽にお問い合わせください。
            </p>
          </div>
        </div>

        {/* Service Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {services.map((service, i) => (
            <ServiceCard key={service.title} service={service} index={i} parentInView={inView} />
          ))}
        </div>

        {/* Note */}
        <div
          className={`mt-10 p-5 bg-white rounded-2xl border border-gray-100 transition-all duration-700 delay-700 ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <div className="flex items-start gap-3">
            <div className="w-5 h-5 flex items-center justify-center mt-0.5 shrink-0">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#88B277" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed">
              料金は介護保険の自己負担割合（1割・2割・3割）により異なります。
              介護保険の適用外となるサービス（日用品費・食費等）については別途ご案内いたします。
              ケアマネジャーや当施設スタッフへお気軽にご相談ください。
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function ServiceCard({
  service,
  index,
  parentInView,
}: {
  service: (typeof services)[0];
  index: number;
  parentInView: boolean;
}) {
  return (
    <div
      className={`bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-[#88B277]/30 transition-all duration-700 hover:-translate-y-1 ${
        parentInView ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-95'
      }`}
      style={{ transitionDelay: `${300 + index * 120}ms` }}
    >
      {/* Image */}
      <div className="relative h-44 overflow-hidden">
        <img
          src={service.image}
          alt={service.title}
          className="w-full h-full object-cover object-top"
        />
        {/* Price Badge */}
        <div className="absolute top-3 right-3 px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full">
          <span className="text-[#88B277] text-xs font-semibold">{service.priceNote}</span>
        </div>
      </div>

      {/* Content */}
      <div className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 flex items-center justify-center bg-[#88B277]/10 rounded-lg text-[#88B277]">
            {service.icon}
          </div>
          <h3 className="font-bold text-gray-800 text-sm leading-tight">{service.title}</h3>
        </div>
        <p className="text-gray-500 text-xs leading-relaxed mb-4">{service.description}</p>
        <div className="space-y-1.5">
          {service.details.map((detail) => (
            <div key={detail} className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-[#88B277] shrink-0" />
              <span className="text-gray-600 text-xs">{detail}</span>
            </div>
          ))}
        </div>
        <div className="mt-4 pt-4 border-t border-gray-100">
          <span className="text-gray-400 text-xs">{service.price}</span>
        </div>
      </div>
    </div>
  );
}
